import React, { useEffect, useMemo, useCallback } from "react";
import { observer, useLocalStore } from "mobx-react";
import { useHistory, useParams } from "react-router-dom";
import _ from "lodash";
import { Button, Select, Tooltip, Spin, Modal, Checkbox } from "antd";
import {
    CheckOutlined,
    CloseOutlined,
    QuestionCircleOutlined,
    LoadingOutlined,
    LinkOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import Wand from "@app/assets/icons/wand";
import classes from "classnames";
import TextInput from "./text-input";
import md from "@app/lib/md";
import format from "@app/lib/format";

import aiStore from "@app/state/store/ai";
import articleStore from "@app/state/store/report/article";
import reportStore from "@app/state/store/report";
import session from "@app/state/store/session";
import { AI_DECISION } from "@app/constants";

import { useAutoLoadingState } from "@app/hooks/useAutoLoadingState";

import "./style/additionalData.scoped.scss";

const SAVE_TIMEOUT = 5 * 1000;

/**
 * Get the session storage key for regenerate warning suppression.
 * Stored per user per project, resets on assignment of new project.
 */
function getRegenerateWarningKey() {
    const userId = session.user?._id;
    const projectId = reportStore.id;
    return `regenerate-warning-suppressed:${userId}:${projectId}`;
}

function isRegenerateWarningSuppressed() {
    try {
        return sessionStorage.getItem(getRegenerateWarningKey()) === "true";
    } catch {
        return false;
    }
}

function suppressRegenerateWarning() {
    try {
        sessionStorage.setItem(getRegenerateWarningKey(), "true");
    } catch {
        // ignore
    }
}

const AdditionalDataField = observer(
    ({ additionalData, options, onChange, readonly = false, variants }) => {
        const history = useHistory();
        const { type } = useParams();

        const state = useLocalStore(() => ({
            defaultValue: additionalData?.value || "",
            changed: false,
            showAIField: false,
            suggestion: undefined,
            sources: [],
        }));
        const { isLoading, isError } = useAutoLoadingState([
            "ArticleStore",
            "updateAdditionalData",
            additionalData._id,
        ]);

        const fieldType = additionalData?.type;
        let timer = null;

        // Determine if the field has an AI question configured and has had a suggestion generated
        const hasAIQuestion = !!additionalData.ai?.question;
        const hasSuggestion = aiStore.isValidSuggestion(additionalData.ai?.suggestion);
        const processing = articleStore.article?.processing?.additionalData;
        const isGenerating = !!additionalData.ai?.generating;

        // Show regenerate button for all fields with AI question that have had a suggestion
        // (States B, C, D, E - not State A where no suggestion has been generated yet)
        const showRegenerate = hasAIQuestion && hasSuggestion && !readonly && !processing;

        useEffect(() => {
            const show =
                aiStore.isValidSuggestion(additionalData.ai?.suggestion) &&
                ![AI_DECISION.ACCEPTED, AI_DECISION.REJECTED].includes(
                    additionalData.ai.decision,
                ) &&
                !additionalData.ai.handled;

            if (show !== state.showAIField) {
                state.showAIField = show;
            }

            if (state.suggestion !== additionalData.ai.suggestion) {
                let suggestion = format.sanitize(additionalData.ai.suggestion);
                const html = md.toHtml(suggestion);

                if (state.suggestion !== html && !state.changed) {
                    state.suggestion = html;
                    state.sources = additionalData.ai.sources ?? [];
                }
            }
        }, [
            additionalData.ai.question,
            additionalData.ai.suggestion,
            additionalData.ai.handled,
            additionalData.ai.decision,
        ]);

        /**
         * Handle changes to text input. Schedules a text save event every few seconds.
         */
        const textChanged = (value) => {
            state.changed = true;

            state.defaultValue = value;
            additionalData.value = value;
            scheduleSaveText();
        };

        /**
         * Schedule a text save action after a set amount of time.
         */
        const scheduleSaveText = () => {
            clearTimeout(timer);
            timer = setTimeout(saveText, SAVE_TIMEOUT);
        };

        /**
         * Immediately emit a save event
         */
        const saveText = () => {
            if (state.changed) {
                state.changed = false;
                clearTimeout(timer);

                onChange && onChange(additionalData);
            }
        };

        const setSelectedValue = (value, decision = AI_DECISION.NONE) => {
            // update the default value
            state.defaultValue = value;
            additionalData.value = value;
            additionalData.ai.decision = decision;

            onChange && onChange(additionalData);
        };

        const handleSuggestion = _.debounce((value, decision) => {
            additionalData.ai.handled = true;
            setSelectedValue(value, decision);
            state.showAIField = false;
        }, 300);

        const onManualSave = () => {
            onChange && onChange(additionalData);
        };

        /**
         * Handle regenerate button click with confirmation dialog
         */
        const handleRegenerate = useCallback(() => {
            if (isRegenerateWarningSuppressed()) {
                // Skip confirmation, regenerate directly
                articleStore.regenerateFieldSuggestion({
                    fieldId: additionalData._id,
                    variantId: additionalData.variantId,
                    type,
                });
                return;
            }

            let doNotShowAgain = false;

            Modal.confirm({
                title: "Regenerate Suggestion?",
                content: (
                    <div>
                        <p>
                            Regenerating will replace the current content and remove the existing
                            source link.
                        </p>
                        <p>This action cannot be undone.</p>
                        <Checkbox
                            onChange={(e) => {
                                doNotShowAgain = e.target.checked;
                            }}
                        >
                            Do not show this warning again (for this session)
                        </Checkbox>
                    </div>
                ),
                okText: "Regenerate",
                cancelText: "Cancel",
                onOk() {
                    if (doNotShowAgain) {
                        suppressRegenerateWarning();
                    }
                    articleStore.regenerateFieldSuggestion({
                        fieldId: additionalData._id,
                        variantId: additionalData.variantId,
                        type,
                    });
                },
            });
        }, [additionalData._id, additionalData.variantId, type]);

        /*
         * Open the file and highlight the source
         */
        function highlightSource(source) {
            const points = source.coordinates.points;
            const width = source.coordinates.layout_width;
            const height = source.coordinates.layout_height;

            const x1 = (points[0][0] / width) * 100; // x from top left corner
            const x2 = (points[2][0] / width) * 100; // x from bottom right corner
            const y1 = (points[0][1] / height) * 100; // y from top left corner
            const y2 = (points[1][1] / height) * 100; // y from bottom left corner

            let area = {
                pageIndex: source.page - 1,
                left: x1,
                top: y1,
                height: y2 - y1,
                width: x2 - x1,
            };

            let link =
                location.pathname + `?view-file=true&focus=true&area=${JSON.stringify(area)}`;
            history.push(link);
        }

        /**
         * Source link component - shown below the field when a suggestion has been
         * accepted/rejected (States C, D, E) and a source exists
         */
        const SourceLink = useMemo(() => {
            const sources = additionalData.ai?.sources ?? [];
            if (sources.length === 0 || state.showAIField) {
                return null;
            }

            // Show source link for accepted, rejected, or handled suggestions
            const isHandled = [AI_DECISION.ACCEPTED].includes(additionalData.ai?.decision);

            if (!isHandled || !hasSuggestion) {
                return null;
            }

            return (
                <div className="persistentSourceLink">
                    <span className="aiQuestionIcon">
                        <Wand />
                        Sources
                        {sources.map((source, index) => (
                            <div
                                key={index}
                                className="link"
                                onClick={() => highlightSource(source)}
                            >
                                [<LinkOutlined />]
                            </div>
                        ))}
                    </span>
                </div>
            );
        }, [
            additionalData.ai?.sources,
            additionalData.ai?.handled,
            additionalData.ai?.decision,
            additionalData.ai?.suggestion,
            state.showAIField,
        ]);

        const AITextField = useMemo(() => {
            return (
                <div className="aiFieldField">
                    <TextInput
                        value={state.suggestion}
                        readOnly={readonly}
                        onChange={(value) => {
                            state.suggestion = value;
                        }}
                    />
                    <div className="aiFieldFooter">
                        <span className="aiQuestionIcon">
                            <Wand />
                            Suggestion
                            {state.sources.map((source) => (
                                <div className="link" onClick={() => highlightSource(source)}>
                                    [<LinkOutlined />]
                                </div>
                            ))}
                        </span>

                        {!readonly && (
                            <div className="aiFieldActions">
                                <Button
                                    type="primary"
                                    danger
                                    icon={<CloseOutlined />}
                                    onClick={() => handleSuggestion("", AI_DECISION.REJECTED)}
                                />
                                <Button
                                    type="primary"
                                    className="checkedIcon"
                                    icon={<CheckOutlined />}
                                    onClick={() =>
                                        handleSuggestion(state.suggestion, AI_DECISION.ACCEPTED)
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            );
        }, [state.suggestion]);

        const SelectField = observer(({ defaultValue }) => {
            return (
                <Select
                    defaultValue={defaultValue}
                    onChange={(value) => setSelectedValue(value)}
                    placeholder="Please select"
                    disabled={readonly}
                >
                    {options.map((option) => (
                        <Select.Option
                            key={option.value}
                            value={option.value}
                            selected={option.value === defaultValue}
                        >
                            {option.value}
                        </Select.Option>
                    ))}
                </Select>
            );
        });

        return (
            <div
                className={classes("fieldContainer", {
                    dangerFieldContainer: state.showAIField,
                    failedToSave: isError,
                    generatingField: isGenerating,
                })}
            >
                {(isLoading || isGenerating) && (
                    <div className="loadingSpinner">
                        <Spin indicator={<LoadingOutlined spin />} size="small" />
                    </div>
                )}

                <div className="fieldLabel">
                    <span className="text">
                        {additionalData?.title}
                        {variants?.length > 0 &&
                            ` (${
                                variants?.find(
                                    (variant) => variant._id === additionalData.variantId,
                                )?.title
                            })`}
                    </span>
                    {/* <span className="text"> */}

                    {isGenerating && (
                        <span className="generatingIndicator">
                            {/* <Wand /> */}
                            <span>Generating…</span>
                        </span>
                    )}
                    {/* </span> */}

                    {(hasAIQuestion || additionalData.helpMessage) && (
                        <span className="aiLabelIcons">
                            {showRegenerate && (
                                <Tooltip title="Regenerate suggestion" placement="topRight">
                                    <SyncOutlined
                                        className="regenerateIcon"
                                        onClick={handleRegenerate}
                                    />
                                </Tooltip>
                            )}
                            {hasAIQuestion && (
                                <Tooltip title={additionalData.ai?.question} placement="topRight">
                                    <span className="aiQuestionIcon">
                                        <Wand />
                                    </span>
                                </Tooltip>
                            )}
                            {additionalData.helpMessage && (
                                <Tooltip title={additionalData.helpMessage} placement="topRight">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            )}
                        </span>
                    )}
                </div>

                <div className="fieldContent">
                    {fieldType === "text" &&
                        (state.showAIField ? (
                            AITextField
                        ) : (
                            <TextInput
                                value={state.defaultValue}
                                onChange={textChanged}
                                readOnly={readonly}
                                onBlur={(value) => {
                                    additionalData.value = value;
                                    saveText();
                                }}
                            />
                        ))}
                    {fieldType === "select" && <SelectField defaultValue={state.defaultValue} />}
                </div>

                {/* Persistent source link - shown after acceptance/rejection */}
                {SourceLink}

                {isError && (
                    <div className={classes("manualSave", { underSelect: fieldType !== "text" })}>
                        <Button size="small" type={"primary"} onClick={onManualSave}>
                            Save
                        </Button>
                    </div>
                )}
            </div>
        );
    },
);

export default AdditionalDataField;
